// ============================================================
// DICE ENGINE - Pure functions, zero React dependency
// ============================================================
import type { DiceGroup, DiceResult } from '../types';

/** One `+`-separated term to its group, or null when it is not a dice term (ignored). */
function parseDiceTerm(part: string): DiceGroup | null {
  const nestedMatch = part.match(/^\[(\d+)d(\d+)\]d(\d+)$/);
  if (nestedMatch) {
    const [countDice, countSides, sides] = nestedMatch.slice(1).map((n) => parseInt(n));
    return { type: 'nested', countDice, countSides, sides };
  }
  const diceMatch = part.match(/^(\d+)d(\d+)$/);
  if (diceMatch) return { type: 'dice', count: parseInt(diceMatch[1]), sides: parseInt(diceMatch[2]) };
  const bareDice = part.match(/^d(\d+)$/);
  if (bareDice) return { type: 'dice', count: 1, sides: parseInt(bareDice[1]) };
  const numMatch = part.match(/^(-?\d+)$/);
  return numMatch ? { type: 'flat', value: parseInt(numMatch[1]) } : null;
}

export function parseDiceExpression(expr: string | null | undefined): DiceGroup[] {
  if (!expr || typeof expr !== 'string') return [];
  // Normalize subtraction into "+-" so flat modifiers can be negative ("2d10-3").
  const cleaned = expr.replace(/\s/g, '').toLowerCase().replace(/-/g, '+-');
  return cleaned
    .split('+')
    .filter((part) => part)
    .map(parseDiceTerm)
    .filter((g): g is DiceGroup => g !== null);
}

/**
 * THE source of randomness for every real die this app rolls.
 *
 * Injectable so the E2E harness can seed the DICE rather than monkey-
 * patching global `Math.random`. That distinction is the whole point:
 * with a global override, any unrelated call anywhere in the app - an id
 * being minted, a retry jitter - steals a value and shifts every later
 * roll. And because the harness's sequence cycles, it does so SILENTLY,
 * handing back a plausible wrong number from earlier in the sequence
 * rather than failing. Two such thefts were found and fixed in one day
 * (v7.52.0, v7.52.1) and a third was still at large.
 *
 * Seeded here, no stray consumer anywhere can ever shift a die again.
 *
 * NEVER drained by the probability engines: their Monte Carlo runs roll
 * through the same live code, so `withForecastDice` (probability.ts) swaps
 * in a seeded source of their own for each forecast. Until 2026-10-02 they
 * drew from this one, and a forecast recomputed after an E2E spec seeded
 * its dice ate thousands of the seeded values.
 */
let randomSource: (() => number) | null = null;

/** Replace the dice source. The E2E harness only; production never calls it. */
export function setDiceRandomSource(fn: (() => number) | null): void {
  randomSource = fn;
}

/**
 * Run `body` with the dice channel temporarily replaced, restoring the
 * previous source after - the forecasts' seam (`withForecastDice`): every
 * Monte Carlo run draws its own seeded sequence, never the real dice, and
 * per-zone forecasts draw IDENTICAL sequences (v8.250.1), so two zones
 * with the same armour report the same number instead of two samples of
 * Monte Carlo noise.
 *
 * Safe against the E2E harness's own seeding because it is synchronous:
 * `body` contains no awaits, and the harness only swaps sources from
 * event handlers, which cannot run mid-call.
 */
export function withDiceSource<T>(fn: () => number, body: () => T): T {
  const prev = randomSource;
  randomSource = fn;
  try {
    return body();
  } finally {
    randomSource = prev;
  }
}

/**
 * Resolved at CALL time, never captured.
 *
 * `let randomSource = Math.random` would bind the original function at
 * module load, and the unit-test harness
 * (`src/test/helpers/deterministicDice.js`) stubs the global AFTERWARDS -
 * so a captured reference silently ignores it. That mistake took 100
 * tests red to notice, which is a fair warning about how invisible this
 * kind of binding is.
 */
function draw(): number {
  return (randomSource ?? Math.random)();
}

export function rollDie(sides: number): number {
  return Math.floor(draw() * sides) + 1;
}

/**
 * A SECOND channel, for initiative only (v8.66.0).
 *
 * Initiative is rolled on admission, so loading four characters draws
 * four d10s before anybody has attacked. On the shared channel that
 * would shift every subsequent roll in every E2E spec that loads a
 * combatant - the exact hazard gotcha #5 describes, except caused by a
 * legitimate roll rather than a stray `Math.random()`.
 *
 * Unset (which is always, in production) it falls through to `draw()`,
 * so there is one channel by default and the seam exists purely so the
 * E2E harness can seed the two independently.
 */
let initiativeSource: (() => number) | null = null;

/** Replace the initiative source. The E2E harness only; production never calls it. */
export function setInitiativeRandomSource(fn: (() => number) | null): void {
  initiativeSource = fn;
}

/** One d10 for an initiative roll. Resolved at call time, like `draw()`. */
export function rollInitiativeDie(): number {
  return Math.floor((initiativeSource ?? draw)() * 10) + 1;
}

/** A raw fraction from the dice source, for the few rolls that are not dice-shaped. */
export function diceRandom(): number {
  return draw();
}

export function rollD100(): number {
  return rollDie(100);
}

export function calcDoS(skill: number, roll: number): number {
  return Math.max(1, Math.floor((skill - roll) / 10));
}

export function calcDoF(roll: number, skill: number): number {
  return Math.max(1, Math.floor((roll - skill) / 10));
}

/**
 * The MEAN of an expression, for what cannot be rolled ahead of time - the
 * map's overlay of a dice-sized template and the forecast (v8.372.0). A
 * die averages (sides + 1) / 2; a nested group averages the count's mean
 * times the die's mean (the roll floors the count at one, which a mean
 * cannot model - close enough for a preview). An expression that parses
 * to nothing is 0.
 */
export function expectedDice(expr: string | null | undefined): number {
  let total = 0;
  for (const g of parseDiceExpression(expr)) {
    if (g.type === 'flat') total += g.value ?? 0;
    else if (g.type === 'dice') total += ((g.count ?? 1) * ((g.sides ?? 0) + 1)) / 2;
    else total += ((((g.countDice ?? 1) * ((g.countSides ?? 0) + 1)) / 2) * ((g.sides ?? 0) + 1)) / 2;
  }
  return total;
}

/** Roll `count` dice of `sides`, in order. */
function rollDice(count: number, sides: number): number[] {
  const rolls: number[] = [];
  for (let i = 0; i < count; i++) rolls.push(rollDie(sides));
  return rolls;
}

const sum = (rolls: number[]) => rolls.reduce((a, b) => a + b, 0);

/** A nested group: roll the count dice first, then that many (at least one) of the main die. */
function rollNested(g: DiceGroup): DiceGroup {
  const countRolls = rollDice(g.countDice!, g.countSides!);
  const actualCount = Math.max(1, sum(countRolls));
  const rolls = rollDice(actualCount, g.sides!);
  const countExpr = `${g.countDice}d${g.countSides}`;
  return { type: 'nested', countExpr, countRolls, countResult: actualCount, sides: g.sides, rolls, total: sum(rolls) };
}

function rollGroup(g: DiceGroup): DiceGroup | null {
  if (g.type === 'nested') return rollNested(g);
  if (g.type === 'dice') {
    const rolls = rollDice(g.count!, g.sides!);
    return { type: 'dice', expr: `${g.count}d${g.sides}`, rolls, total: sum(rolls), sides: g.sides };
  }
  return g.type === 'flat' ? { type: 'flat', value: g.value } : null;
}

/** Roll every group left to right - the dice are drawn in exactly that order. */
export function rollDiceExpression(expr: string): DiceResult {
  const results: DiceGroup[] = [];
  let total = 0;
  for (const g of parseDiceExpression(expr)) {
    const rolled = rollGroup(g);
    if (!rolled) continue;
    results.push(rolled);
    total += rolled.type === 'flat' ? rolled.value! : rolled.total!;
  }
  return { results, total };
}
